import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, LogOut, Plus, RefreshCw, Trash2 } from "lucide-react";
import useSeo from "../hooks/useSeo.js";
import LeadsManager from "../components/LeadsManager.jsx";
import { CLUBS } from "../content.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Fitness Event", "Sports Event", "Wellness", "Family", "Community", "Tournament"];

export default function Admin() {
  useSeo({ title: "Owner Console | Oneiros Sports Club", description: "Oneiros team console" });
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-mist">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (user === null) return <Login onLogin={setUser} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-mist pb-24">
      <header className="border-b border-hairline bg-navy px-6 py-5 text-white lg:px-12">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div>
            <p className="font-display text-2xl">{user.role === "staff" ? "Oneiros Staff Console" : "Oneiros Owner Console"}</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">
              {user.role === "staff" ? `${user.club || "Club"} · Leads` : "Clubs · Events · Testimonials · Enquiries · Team"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-white hover:text-navy">
              <ArrowLeft className="h-3.5 w-3.5" /> Site
            </a>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white hover:bg-brand-dark">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 pt-8 lg:px-12">
        <div className="flex flex-wrap gap-2">
          {(user.role === "staff"
            ? [["enquiries", `Leads — ${user.club || "My Club"}`]]
            : [
                ["enquiries", "Enquiries"],
                ["events", "Events"],
                ["clubs", "Clubs"],
                ["testimonials", "Testimonials"],
                ["team", "Team"],
              ]
          ).map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`rounded-full border px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${
                tab === id ? "border-brand bg-brand text-white" : "border-hairline bg-white text-mute hover:text-navy"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="mt-8">
          {tab === "enquiries" && <LeadsManager clubScope={user.role === "staff" ? user.club : null} canDelete={user.role === "admin"} />}
          {user.role === "admin" && tab === "events" && <EventsManager />}
          {user.role === "admin" && tab === "clubs" && <ClubsManager />}
          {user.role === "admin" && tab === "testimonials" && <TestimonialsManager />}
          {user.role === "admin" && tab === "team" && <StaffManager />}
        </div>
      </div>
    </div>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data.user);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-mist px-6">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md border border-hairline bg-white p-10">
        <p className="eyebrow">Oneiros Sports Club</p>
        <h1 className="mt-2 font-display text-4xl text-navy">Owner Console</h1>
        <p className="mt-2 text-sm text-mute">Sign in to manage clubs, events and testimonials.</p>
        <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="mt-8 w-full border border-hairline px-4 py-3.5 text-sm outline-none focus:border-brand" />
        <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="mt-3 w-full border border-hairline px-4 py-3.5 text-sm outline-none focus:border-brand" />
        <button data-testid="admin-login-submit" disabled={busy} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-brand-dark disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function StaffManager() {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", club: "Oneiros Katargam" });
  const [busy, setBusy] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPass, setNewPass] = useState("");

  const load = () => axios.get(`${API}/admin/staff`, { withCredentials: true }).then(({ data }) => setStaff(data)).catch(() => toast.error("Could not load team"));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/staff`, form, { withCredentials: true });
      setStaff((s) => [...s, data]);
      setForm({ name: "", email: "", password: "", club: form.club });
      toast.success(`${data.name} added for ${data.club}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add employee");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      const { data } = await axios.put(`${API}/admin/staff/${s.id}`, { active: !s.active }, { withCredentials: true });
      setStaff((st) => st.map((x) => (x.id === data.id ? data : x)));
      toast.success(data.active ? `${data.name} activated` : `${data.name} deactivated`);
    } catch {
      toast.error("Update failed");
    }
  };

  const resetPassword = async (s) => {
    try {
      await axios.put(`${API}/admin/staff/${s.id}`, { password: newPass }, { withCredentials: true });
      toast.success(`Password updated for ${s.name}`);
      setResetFor(null);
      setNewPass("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password update failed");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}'s access to ${s.club}?`)) return;
    try {
      await axios.delete(`${API}/admin/staff/${s.id}`, { withCredentials: true });
      toast.success("Access removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const input = "w-full border border-hairline px-4 py-3 text-sm outline-none focus:border-brand";

  return (
    <div data-testid="admin-staff-panel" className="border border-hairline bg-white">
      <div className="border-b border-hairline px-6 py-4">
        <h2 className="font-display text-2xl text-navy">Team</h2>
        <p className="mt-1 text-xs text-mute">Create club-wise employee logins. Staff see only their club's leads — status, notes, Call & WhatsApp.</p>
      </div>

      <form data-testid="admin-staff-form" onSubmit={create} className="grid gap-3 border-b border-hairline px-6 py-5 md:grid-cols-4">
        <input data-testid="admin-staff-name" required minLength={1} placeholder="Employee name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
        <input data-testid="admin-staff-email" type="email" required placeholder="Login email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
        <input data-testid="admin-staff-password" type="text" required minLength={8} placeholder="Password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
        <div className="flex gap-2">
          <select data-testid="admin-staff-club" value={form.club} onChange={(e) => setForm({ ...form, club: e.target.value })} className={`${input} flex-1`}>
            {CLUBS.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <button data-testid="admin-staff-submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 text-[10px] font-bold uppercase tracking-[0.18em] text-white hover:bg-brand-dark disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
          </button>
        </div>
      </form>

      {!staff.length ? (
        <p className="px-6 py-10 text-center text-sm text-mute">No employees yet — add your first club manager above.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.18em] text-faint">
                <th className="px-6 py-3">Employee</th><th className="px-4 py-3">Login</th><th className="px-4 py-3">Club</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} data-testid={`admin-staff-row-${s.id}`} className="border-b border-hairline/60 align-top">
                  <td className="px-6 py-4 font-semibold text-navy">{s.name}</td>
                  <td className="px-4 py-4 text-xs">{s.email}</td>
                  <td className="px-4 py-4 text-xs">{s.club}</td>
                  <td className="px-4 py-4">
                    <button data-testid={`admin-staff-toggle-${s.id}`} onClick={() => toggleActive(s)} className={`rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] ${s.active === false ? "border-slate-200 bg-slate-100 text-slate-500" : "border-emerald-200 bg-emerald-50 text-emerald-600"}`}>
                      {s.active === false ? "Deactivated" : "Active"}
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex justify-end gap-1.5">
                      <button data-testid={`admin-staff-reset-${s.id}`} onClick={() => setResetFor(resetFor === s.id ? null : s.id)} className="border border-hairline px-2.5 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-navy hover:border-brand hover:text-brand">
                        Reset Password
                      </button>
                      <button data-testid={`admin-staff-delete-${s.id}`} onClick={() => remove(s)} title="Remove access" className="border border-hairline px-2.5 py-2 text-navy hover:border-red-400 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {resetFor === s.id && (
                      <div className="mt-2 flex gap-2">
                        <input data-testid={`admin-staff-newpass-${s.id}`} type="text" minLength={8} required placeholder="New password (min 8 chars)" value={newPass} onChange={(e) => setNewPass(e.target.value)} className={`${input} flex-1`} />
                        <button data-testid={`admin-staff-savepass-${s.id}`} onClick={() => resetPassword(s)} className="rounded-full bg-navy px-4 text-[10px] font-bold uppercase tracking-[0.12em] text-white">Save</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EventsManager() {
  const [events, setEvents] = useState([]);
  const empty = { name: "", category: "Fitness Event", date: "", time: "", location: "Oneiros Clubs, Surat", description: "" };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => axios.get(`${API}/events`).then(({ data }) => setEvents(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await axios.put(`${API}/admin/events/${editId}`, form, { withCredentials: true });
      else await axios.post(`${API}/admin/events`, form, { withCredentials: true });
      toast.success(editId ? "Event updated" : "Event added — it's now live on the website");
      setForm(empty);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/events/${id}`, { withCredentials: true }).catch(() => {});
    toast.success("Event removed");
    load();
  };

  const input = "w-full border border-hairline px-4 py-3 text-sm outline-none focus:border-brand";
  return (
    <div data-testid="admin-events-panel" className="grid gap-8 lg:grid-cols-12">
      <form data-testid="admin-event-form" onSubmit={submit} className="flex flex-col gap-3 border border-hairline bg-white p-6 lg:col-span-5">
        <h2 className="font-display text-2xl text-navy">{editId ? "Edit Event" : "Add Event"}</h2>
        <input required data-testid="admin-event-name" placeholder="Event name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
        <select data-testid="admin-event-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input}>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="admin-event-date" placeholder="Date (e.g. 15 Feb, Sunday)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={input} />
          <input data-testid="admin-event-time" placeholder="Time (e.g. 7 AM – 10 AM)" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={input} />
        </div>
        <input data-testid="admin-event-location" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={input} />
        <textarea data-testid="admin-event-description" rows={3} placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${input} resize-none`} />
        <button data-testid="admin-event-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:bg-brand-dark">
          <Plus className="h-4 w-4" /> {editId ? "Update Event" : "Publish Event"}
        </button>
        {editId && (
          <button type="button" onClick={() => { setEditId(null); setForm(empty); }} className="text-xs text-mute underline">
            Cancel edit
          </button>
        )}
      </form>
      <div className="flex flex-col gap-3 lg:col-span-7">
        {events.map((ev) => (
          <div key={ev.id} data-testid={`admin-event-row-${ev.id}`} className="flex items-start justify-between gap-4 border border-hairline bg-white p-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand">{ev.category}{ev.sample ? " · sample" : ""}</p>
              <p className="mt-1 font-display text-xl text-navy">{ev.name}</p>
              <p className="mt-1 text-xs text-mute">{ev.date} · {ev.time} · {ev.location}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              {!ev.sample && (
                <button data-testid={`admin-event-edit-${ev.id}`} onClick={() => { setEditId(ev.id); setForm({ name: ev.name, category: ev.category, date: ev.date, time: ev.time, location: ev.location || "", description: ev.description || "" }); }} className="border border-hairline px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-navy hover:border-brand hover:text-brand">
                  Edit
                </button>
              )}
              <button data-testid={`admin-event-delete-${ev.id}`} onClick={() => remove(ev.id)} aria-label="Delete event" className="border border-hairline px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-navy hover:border-red-400 hover:text-red-500">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        {events.length === 0 && <p className="border border-dashed border-hairline p-8 text-center text-sm text-mute">No events yet — publish your first real event on the left.</p>}
      </div>
    </div>
  );
}

function ClubsManager() {
  const [clubs, setClubs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  useEffect(() => {
    axios.get(`${API}/clubs`).then(({ data }) => {
      setClubs(data);
      setSelected(data[0]?.id);
      setForm(data[0] || {});
    });
  }, []);
  const pick = (id) => {
    setSelected(id);
    setForm(clubs.find((c) => c.id === id) || {});
  };
  const save = async () => {
    try {
      await axios.put(`${API}/admin/clubs/${selected}`, {
        address: form.address || "",
        phone: form.phone || "",
        timings: form.timings || "",
        description: form.description || "",
        positioning: form.positioning || "",
      }, { withCredentials: true });
      toast.success("Club details updated — live on the website");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };
  const input = "w-full border border-hairline px-4 py-3 text-sm outline-none focus:border-brand";
  return (
    <div data-testid="admin-clubs-panel" className="border border-hairline bg-white p-6">
      <div className="flex flex-wrap items-center gap-2">
        {clubs.map((c) => (
          <button key={c.id} data-testid={`admin-club-pill-${c.id}`} onClick={() => pick(c.id)} className={`rounded-full border px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.15em] ${selected === c.id ? "border-navy bg-navy text-white" : "border-hairline text-mute"}`}>
            {c.name}
          </button>
        ))}
      </div>
      {form && (
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          <input data-testid="admin-club-address" placeholder="Address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className={`${input} md:col-span-2`} />
          <input data-testid="admin-club-phone" placeholder="Phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} />
          <input data-testid="admin-club-timings" placeholder="Timings" value={form.timings || ""} onChange={(e) => setForm({ ...form, timings: e.target.value })} className={input} />
          <input data-testid="admin-club-positioning" placeholder="Positioning line" value={form.positioning || ""} onChange={(e) => setForm({ ...form, positioning: e.target.value })} className={`${input} md:col-span-2`} />
          <textarea data-testid="admin-club-description" rows={3} placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${input} resize-none md:col-span-2`} />
          <button data-testid="admin-club-save" onClick={save} className="rounded-full bg-brand py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:bg-brand-dark md:col-span-2">
            Save Club Details
          </button>
          <p className="text-xs italic text-faint md:col-span-2">Note: the club pages also read from the site content file — database updates flow to CMS-driven surfaces and the AI concierge immediately.</p>
        </div>
      )}
    </div>
  );
}

function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", quote: "" });
  const load = () => axios.get(`${API}/testimonials`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/testimonials`, form, { withCredentials: true });
      toast.success("Testimonial added");
      setForm({ name: "", quote: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    }
  };
  const remove = async (id) => {
    await axios.delete(`${API}/admin/testimonials/${id}`, { withCredentials: true }).catch(() => {});
    toast.success("Testimonial removed");
    load();
  };
  const input = "w-full border border-hairline px-4 py-3 text-sm outline-none focus:border-brand";
  return (
    <div data-testid="admin-testimonials-panel" className="grid gap-8 lg:grid-cols-12">
      <form data-testid="admin-testimonial-form" onSubmit={add} className="flex flex-col gap-3 border border-hairline bg-white p-6 lg:col-span-5">
        <h2 className="font-display text-2xl text-navy">Add Testimonial</h2>
        <input required data-testid="admin-testimonial-name" placeholder="Member name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
        <textarea required data-testid="admin-testimonial-quote" rows={4} placeholder="Quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className={`${input} resize-none`} />
        <button className="inline-flex items-center justify-center gap-2 rounded-full bg-brand py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:bg-brand-dark">
          <Plus className="h-4 w-4" /> Add Testimonial
        </button>
      </form>
      <div className="flex flex-col gap-3 lg:col-span-7">
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-row-${t.id}`} className="flex items-start justify-between gap-4 border border-hairline bg-white p-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand">{t.name}</p>
              <p className="mt-1 text-sm text-body">“{t.quote}”</p>
            </div>
            <button data-testid={`admin-testimonial-delete-${t.id}`} onClick={() => remove(t.id)} aria-label="Delete testimonial" className="border border-hairline px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-navy hover:border-red-400 hover:text-red-500">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
